import {
  ChakraFlex,
  FAIconName,
  FireflyButton,
  Tag,
  TagColor,
  Text,
} from '@equip.health/ui';
import { useHistory } from 'react-router-dom';

import { PatientTabs } from '~/lib/constants/patients.constants';
import { PatientTabsCollection } from '~/lib/hooks/usePatientTabsCollection';
import { SurveyName } from '~/lib/constants/survey/SurveyName';
import {
  PatientAlert,
  PatientAlertAction,
} from '~/lib/types/patient/PatientAlert';
import { getTimestamp } from '~/lib/util/notification.util';
import { Path } from '~/routes/constants';

type PatientAlertBannerProps = {
  patientAlert: PatientAlert;
  id: string;
  onClose: () => void;
  onClickAddNew: (surveyName: SurveyName) => void;
  patientId: string;
  onContinueSurvey: (externalId: string) => void;
};

const topBottomPadding = '5px 0px';

const PatientAlertBanner = ({
  patientAlert,
  id: TOP_LEVEL_ID,
  onClose: closeMenu,
  onClickAddNew,
  onContinueSurvey,
  patientId,
}: PatientAlertBannerProps) => {
  const history = useHistory();

  const { action, description, name, responseExternalId, title, updatedDate } =
    patientAlert;

  const isEmergencyContactAlert =
    action === PatientAlertAction.VIEW_PATIENT_PROFILE;

  const handleOnClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();

    const enumKey = Object.entries(SurveyName).find(([, v]) => v === name)?.[0];
    const query = enumKey ? `?surveyCode=${enumKey}` : '';
    history.push(
      `${Path.MAUD_PATIENTS}/${patientId}/${
        PatientTabsCollection[PatientTabs.ASSESSMENTS].subPath
      }${query}`,
    );

    closeMenu();
  };

  const handleViewPatientProfile = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    history.push(`${Path.MAUD_PATIENTS}/${patientId}/profile`);
    closeMenu();
  };
  const renderActionButton = () => {
    if (isEmergencyContactAlert) {
      return (
        <FireflyButton
          id={`${TOP_LEVEL_ID}__view-patient-profile-button`}
          onClick={handleViewPatientProfile}
          size="small"
          type="submit"
          variant="secondary"
        >
          View patient profile
        </FireflyButton>
      );
    }

    if (responseExternalId) {
      return (
        <FireflyButton
          id={`${TOP_LEVEL_ID}__resume-survey-button`}
          leftIcon={FAIconName.penLine}
          onClick={() => {
            onContinueSurvey(responseExternalId);
            closeMenu();
          }}
          size="small"
          type="submit"
          variant="secondary"
        >
          Resume {action}
        </FireflyButton>
      );
    }

    return (
      <FireflyButton
        id={`${TOP_LEVEL_ID}__new-survey-button`}
        leftIcon={FAIconName.plusSolid}
        onClick={() => {
          onClickAddNew(action);
          closeMenu();
        }}
        size="small"
        type="submit"
        variant="secondary"
      >
        Add {action}
      </FireflyButton>
    );
  };

  return (
    <ChakraFlex direction="column" id={`${TOP_LEVEL_ID}__container`} gap="18px">
      <ChakraFlex gap="12px" direction="column">
        <ChakraFlex alignItems="center" justifyContent="space-between">
          <Text
            id={`${TOP_LEVEL_ID}__description-label-1`}
            variant="bodyMedium"
          >
            {title}
          </Text>
          {!isEmergencyContactAlert && (
            <Tag
              color={TagColor.red}
              id={`${TOP_LEVEL_ID}-primary`}
              value={getTimestamp(updatedDate)}
            />
          )}
        </ChakraFlex>
        <Text
          color="neutral.secondary"
          id={`${TOP_LEVEL_ID}__description-label-2`}
        >
          {description}
        </Text>
      </ChakraFlex>

      <ChakraFlex
        gap="20px"
        id={`${TOP_LEVEL_ID}__survey-data-row-3`}
        justifyContent="flex-end"
        padding={topBottomPadding}
      >
        {!isEmergencyContactAlert && (
          <FireflyButton
            id={`${TOP_LEVEL_ID}__view-survey-button`}
            onClick={handleOnClick}
            size="small"
            type="submit"
            variant="tertiary"
          >
            View {name}
          </FireflyButton>
        )}

        {renderActionButton()}
      </ChakraFlex>
    </ChakraFlex>
  );
};

export default PatientAlertBanner;
